import {
  CHARACTER_KINDS,
  FACTIONS,
  ROLES,
  WOW_CLASSES,
  WOW_RACES,
  type Character,
  type CharacterInput,
} from '@moussenaires/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CharacterMediaField } from './CharacterMediaField.js';
import { Modal } from './Modal.js';
import './CharacterFormModal.css';

interface CharacterFormModalProps {
  character?: Character;
  onClose: () => void;
  onSubmit: (input: Partial<CharacterInput>) => void;
  isSaving: boolean;
}

export function CharacterFormModal({
  character,
  onClose,
  onSubmit,
  isSaving,
}: CharacterFormModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(character?.name ?? '');
  const [realm, setRealm] = useState(character?.realm ?? '');
  const [wowClass, setWowClass] = useState(character?.class ?? WOW_CLASSES[0]);
  const [race, setRace] = useState(character?.race ?? '');
  const [spec, setSpec] = useState(character?.spec ?? '');
  const [faction, setFaction] = useState(character?.faction ?? 'neutral');
  const [level, setLevel] = useState(character?.level ?? 80);
  const [role, setRole] = useState(character?.role ?? '');
  const [kind, setKind] = useState(character?.kind ?? 'main');
  const [itemLevel, setItemLevel] = useState(character?.stats.itemLevel?.toString() ?? '');
  const [mythicPlusScore, setMythicPlusScore] = useState(
    character?.stats.mythicPlusScore?.toString() ?? '',
  );
  const [bestKeyLevel, setBestKeyLevel] = useState(character?.stats.bestKeyLevel?.toString() ?? '');
  const [profileUrl, setProfileUrl] = useState(character?.profileUrl ?? '');

  function toNullableInt(value: string): number | null {
    if (!value.trim()) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function submit() {
    onSubmit({
      name: name.trim(),
      realm: realm.trim(),
      class: wowClass,
      race: (race || null) as CharacterInput['race'],
      spec: spec.trim() || null,
      faction,
      level,
      role: (role || null) as CharacterInput['role'],
      kind,
      profileUrl: profileUrl.trim() || null,
      stats: {
        itemLevel: toNullableInt(itemLevel),
        mythicPlusScore: toNullableInt(mythicPlusScore),
        bestKeyLevel: toNullableInt(bestKeyLevel),
      },
    });
  }

  return (
    <Modal
      title={character ? t('roster.editCharacter') : t('roster.addCharacter')}
      onClose={onClose}
      width="lg"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!name.trim() || !realm.trim() || isSaving}
            onClick={submit}
          >
            {t('common.save')}
          </button>
        </>
      }
    >
      <div className="character-form-grid">
        <div className="field">
          <label htmlFor="character-name">{t('roster.name')}</label>
          <input
            id="character-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
          />
        </div>
        <div className="field">
          <label htmlFor="character-realm">{t('roster.realm')}</label>
          <input
            id="character-realm"
            value={realm}
            onChange={(event) => setRealm(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="character-class">{t('roster.class')}</label>
          <select
            id="character-class"
            value={wowClass}
            onChange={(event) => setWowClass(event.target.value as typeof wowClass)}
          >
            {WOW_CLASSES.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="character-spec">{t('roster.spec')}</label>
          <input
            id="character-spec"
            value={spec}
            onChange={(event) => setSpec(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="character-race">{t('roster.race')}</label>
          <select
            id="character-race"
            value={race}
            onChange={(event) => setRace(event.target.value)}
          >
            <option value="">—</option>
            {WOW_RACES.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="character-faction">{t('roster.faction')}</label>
          <select
            id="character-faction"
            value={faction}
            onChange={(event) => setFaction(event.target.value as typeof faction)}
          >
            {FACTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="character-role">{t('roster.role')}</label>
          <select
            id="character-role"
            value={role}
            onChange={(event) => setRole(event.target.value as typeof role)}
          >
            <option value="">—</option>
            {ROLES.map((option) => (
              <option key={option} value={option}>
                {t(`roster.${option}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="character-kind">{t('roster.kind')}</label>
          <select
            id="character-kind"
            value={kind}
            onChange={(event) => setKind(event.target.value as typeof kind)}
          >
            {CHARACTER_KINDS.map((option) => (
              <option key={option} value={option}>
                {t(`roster.${option}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="character-level">{t('roster.level')}</label>
          <input
            id="character-level"
            type="number"
            min={1}
            max={80}
            value={level}
            onChange={(event) => setLevel(Number(event.target.value))}
          />
        </div>
        <div className="field">
          <label htmlFor="character-ilvl">{t('roster.itemLevel')}</label>
          <input
            id="character-ilvl"
            type="number"
            value={itemLevel}
            onChange={(event) => setItemLevel(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="character-mplus">{t('roster.mythicPlusScore')}</label>
          <input
            id="character-mplus"
            type="number"
            value={mythicPlusScore}
            onChange={(event) => setMythicPlusScore(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="character-key">{t('roster.bestKeyLevel')}</label>
          <input
            id="character-key"
            type="number"
            value={bestKeyLevel}
            onChange={(event) => setBestKeyLevel(event.target.value)}
          />
        </div>
        <div className="field character-form-wide">
          <label htmlFor="character-profile">{t('roster.profileUrl')}</label>
          <input
            id="character-profile"
            value={profileUrl}
            onChange={(event) => setProfileUrl(event.target.value)}
            placeholder="https://raider.io/…"
          />
        </div>

        {/* Only an already-created character has an id to attach media to. */}
        {character && <CharacterMediaField character={character} />}
      </div>
    </Modal>
  );
}
